import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-common/fulfillment-delivery-method-type";
import { FulfillmentGroupStatus } from "@redotech/redo-fulfillment-common/fulfillment-group-status";
import {
  FulfillmentGroup,
  FulfillmentGroupData,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { ShipmentTrackerStatus } from "@redotech/redo-fulfillment-group-common/outbound-shipment-label";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";

export function canVoidFreight(
  row: FulfillmentGroupData | FulfillmentGroup,
): boolean {
  const hasFreightShipment = !!row.shipmentDetails?.freightShipment;
  const alreadyVoided = row.shipmentDetails?.voided;
  const isClosed =
    row.status === FulfillmentGroupStatus.Closed ||
    row.status === FulfillmentGroupStatus.Cancelled;

  return !!hasFreightShipment && !alreadyVoided && isClosed;
}

export function canVoidLabel(row: FulfillmentGroupData | FulfillmentGroup) {
  const hasBoughtLabel = !!row.shipment?.boughtRate;
  const alreadyVoided = row.shipment?.voided;
  const isClosed =
    row.status === FulfillmentGroupStatus.Closed ||
    row.status === FulfillmentGroupStatus.Cancelled;
  const trackingCode = row.shipment?.tracker?.trackingCode;

  const hasShippedAccordingToTracker =
    !!trackingCode &&
    !!row.shipment?.tracker?.status &&
    [
      ShipmentTrackerStatus.IN_TRANSIT,
      ShipmentTrackerStatus.STALLED_IN_TRANSIT,
      ShipmentTrackerStatus.OUT_FOR_DELIVERY,
      ShipmentTrackerStatus.DELIVERED,
    ].includes(row.shipment?.tracker?.status);

  const canVoidDhlLabel = canVoidDhl(row);
  const canVoidTiktokLabel = canVoidTiktok(row);

  return (
    !alreadyVoided &&
    isClosed &&
    !hasShippedAccordingToTracker &&
    hasBoughtLabel &&
    canVoidDhlLabel &&
    canVoidTiktokLabel
  );
}

// Tiktok shipping labels cannot be voided
const canVoidTiktok = (row: FulfillmentGroupData | FulfillmentGroup) => {
  const boughtRate = row.shipment?.boughtRate;
  if (boughtRate?.provider === RateProvider.TIKTOK) {
    return false;
  }

  return true;
};

const canVoidDhl = (row: FulfillmentGroupData | FulfillmentGroup) => {
  const boughtRate = row.shipment?.boughtRate;
  if (!boughtRate || boughtRate.provider !== RateProvider.DHL_ECOMMERCE) {
    return true;
  }

  let originCountry: string | null | undefined;
  let destinationCountry: string | null | undefined;

  // Check if row has direct address fields (FulfillmentGroupData)
  if ("originAddress" in row && "destinationAddress" in row) {
    originCountry = row.originAddress?.countryCode;
    destinationCountry = row.destinationAddress?.countryCode;
  } else if ("summary" in row) {
    originCountry = row.summary.originAddress?.countryCode;
    destinationCountry = row.summary.destinationAddress?.countryCode;
  }

  // International rates cannot be voided
  if (
    !originCountry ||
    !destinationCountry ||
    originCountry !== destinationCountry
  ) {
    return false;
  }

  // If we've already manifested the label, we can't void it
  if (row.shipment?.scanFormId) {
    return false;
  }

  // If the shipment was created more than 45 days ago, we can't void it
  const createdAt = row.shipment?.createdAt;
  if (!createdAt) {
    return false;
  }

  const fortyFiveDaysAgo = new Date();
  fortyFiveDaysAgo.setDate(fortyFiveDaysAgo.getDate() - 45);

  if (createdAt < fortyFiveDaysAgo) {
    return false;
  }

  return true;
};

export function getGroupDeliveryMethod(
  group: FulfillmentGroup,
): RedoShopifyDeliveryMethodType {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return (
    group.overrideDeliveryMethod ??
    (group.fulfillmentOrders.some(
      ({ deliveryMethod }) =>
        deliveryMethod.kind === RedoShopifyDeliveryMethodType.Shipping,
    )
      ? RedoShopifyDeliveryMethodType.Shipping
      : group.fulfillmentOrders[0]?.deliveryMethod?.kind)
  );
}
